'use client';

import { useEffect, useRef, useState } from 'react';
import { Eyebrow, Check, TwitchIcon, YouTubeIcon, TikTokIcon } from './ui';
import { TIER_COLORS } from '@/lib/tier-colors';

/* Shared: stagger items in on scroll, then cycle a pink highlight through them.
   Mirrors the live VoiceHoldSection / TtsModesSection pattern. */
function useStaggerCycle(ref, { selector, staggerMs, cycleMs, activeClass, highlightClass }) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cycleTimer = null;
    const timeouts = new Set();
    const setT = (fn, ms) => {
      const id = setTimeout(() => {
        timeouts.delete(id);
        fn();
      }, ms);
      timeouts.add(id);
    };
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting || cycleTimer) return;
          const items = el.querySelectorAll(selector);
          items.forEach((it, i) => setT(() => it.classList.add(activeClass), i * staggerMs));
          setT(
            () => {
              let cur = 0;
              const tick = () => {
                items.forEach((p) => p.classList.remove(highlightClass));
                items[cur].classList.add(highlightClass);
                cur = (cur + 1) % items.length;
              };
              tick();
              cycleTimer = setInterval(tick, cycleMs);
            },
            items.length * staggerMs + 250,
          );
          obs.unobserve(e.target);
        });
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (cycleTimer) clearInterval(cycleTimer);
      timeouts.forEach((id) => clearTimeout(id));
    };
  }, [ref, selector, staggerMs, cycleMs, activeClass, highlightClass]);
}

/* ─────────────────────────────────────────────────────────────────────────
   ALERTS — real on-stream alert popups (animation built in the asset pass;
   the old bits/gifts tier cascade moved out — it demoed monetary chat FX,
   not what people think of as "alerts").
   ──────────────────────────────────────────────────────────────────────── */
const ALERT_CHIPS = [
  '11 events',
  '8 templates',
  'image · GIF · MP4',
  'screen-shake & particles',
  'reads itself aloud',
  'posts to chat',
];

/* One REAL alert, exactly as the alerts system renders it — the owner's live
   sub config (heroCard template · slideInTop · 4px white border · red fill at
   0.6 · all three shadows · the "money please" media) reproduced at demo scale
   with the same chrome recipe as card-templates/hero-card.js. Fires on a loop;
   the media plays once per firing and holds its last frame (mediaLoop off),
   matching the real card. Reduced motion: the card as a still. */
const ALERT_SHOW_MS = 5200;
const ALERT_GAP_MS = 1400;

// One shared show/gap firing loop for the alert-style demos: on screen for
// showMs, blank for gapMs, then the cycle key increments so the surface
// remounts and its entrance animations replay. Reduced motion: never cycles.
function useShowGapCycle(showMs, gapMs) {
  const [cycle, setCycle] = useState(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let hideT = null;
    const showT = setInterval(() => {
      setVisible(false);
      hideT = setTimeout(() => {
        setCycle((c) => c + 1);
        setVisible(true);
      }, gapMs);
    }, showMs + gapMs);
    return () => {
      clearInterval(showT);
      if (hideT) clearTimeout(hideT);
    };
  }, [showMs, gapMs]);
  return { cycle, visible };
}

function AlertPopDemo() {
  const { cycle, visible } = useShowGapCycle(ALERT_SHOW_MS, ALERT_GAP_MS);
  const videoRef = useRef(null);

  // Each firing replays the media from the top.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !visible) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    video.muted = true;
    video.currentTime = 0;
    video.play().catch(() => {});
  }, [visible, cycle]);

  return (
    <div className="relative mx-auto flex min-h-[340px] max-w-xl items-center justify-center overflow-hidden rounded-2xl border border-gum-gray-800 bg-[#0d0d12] px-6 py-8">
      <span className="absolute right-3.5 top-3 text-[9px] font-extrabold uppercase tracking-[0.15em] text-gum-gray-600">
        On stream
      </span>
      {visible && (
        <div key={cycle} className="alert-slide-top flex flex-col items-center text-center">
          <div
            style={{
              width: 'fit-content',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 7,
              color: 'white',
              fontFamily: 'system-ui, sans-serif',
              background: 'rgba(255, 0, 0, 0.6)',
              border: '4px solid #ffffff',
              borderRadius: 12,
              boxShadow: '0 6px 16px rgba(0, 0, 0, 0.6)',
              padding: '12px 16px',
            }}
          >
            <video
              ref={videoRef}
              src="/landing/alert-money-please.mp4"
              poster="/landing/alert-money-poster.jpg"
              muted
              playsInline
              preload="auto"
              style={{
                maxWidth: 300,
                width: '100%',
                display: 'block',
                borderRadius: 12,
                filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.6))',
              }}
            />
            <div
              style={{
                fontSize: 24,
                fontWeight: 800,
                lineHeight: 1.2,
                textShadow: '0 2px 8px rgba(0, 0, 0, 0.6)',
              }}
            >
              Thanks for the Sub, Dogl1ke!
            </div>
          </div>
          <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-gum-gray-500">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="#ff90e8" aria-hidden="true">
              <path d="M3 10v4h4l5 5V5L7 10H3z" />
            </svg>
            reading aloud…
            <MicBars active h={10} />
          </div>
        </div>
      )}
    </div>
  );
}

export function Alerts() {
  return (
    <section id="alerts" className="px-6 py-24">
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12 reveal">
          <Eyebrow>Stream alerts</Eyebrow>
          <h2 className="mt-5 text-4xl md:text-6xl font-black tracking-tight leading-[1.05]">
            Every alert SE had.
            <br />
            <span className="gradient-text">Across every platform.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-gum-gray-400 leading-relaxed">
            Follows, subs, raids, bits, Super Chats, TikTok gifts, channel points, hype trains — one “All Platforms”
            alert covers Twitch, YouTube &amp; TikTok at once. Eight templates, your own media and sounds, and they read
            themselves aloud.
          </p>
        </div>

        <div className="max-w-2xl mx-auto reveal" data-delay="150">
          <AlertPopDemo />
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-2 reveal" data-delay="200">
          {ALERT_CHIPS.map((c) => (
            <span
              key={c}
              className="rounded-full border border-gum-gray-700 px-3.5 py-1.5 text-xs font-semibold text-gum-gray-300"
            >
              {c}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   VOICE HOLD — the crown jewel (animated 4-step demo)
   ──────────────────────────────────────────────────────────────────────── */
function MicBars({ active, h = 28 }) {
  return (
    <span className="flex items-end gap-1" style={{ height: h }}>
      {[0, 1, 2, 3, 4].map((b) => (
        <span
          key={b}
          className="w-1 rounded-full"
          style={{
            height: h,
            background: active ? '#ff90e8' : '#3d3d37',
            transformOrigin: 'bottom',
            animation: active ? `micWave 0.7s ease-in-out ${b * 0.09}s infinite` : 'none',
            transform: active ? undefined : 'scaleY(0.18)',
          }}
        />
      ))}
    </span>
  );
}

// Cartoon headset-streamer — line-art with a REAL talking performance
// (owner ask 2026-08-06): while `talking`, the mouth jabbers on an
// irregular cycle, the brows pop and tilt, the whole head bobs, and the
// speech arcs pulse; blinks run in both states. All transform/opacity
// keyframes (vh-* in landing-client.js) hung off the .vh-talk root class,
// so the performance starts and stops exactly with the demo's phases.
// Idle: mouth settles to the flat line, brows level out, blinks continue.
function StreamerFigure({ talking }) {
  const stroke = '#d1d1c7';
  return (
    <svg width="68" height="68" viewBox="0 0 80 80" fill="none" aria-hidden="true" className={talking ? 'vh-talk' : ''}>
      {/* shoulders — outside the head bob, so the body sits still */}
      <path d="M19 78 Q20 58 40 58 Q60 58 61 78" stroke={stroke} strokeWidth="3.5" strokeLinecap="round" />
      {/* everything on the head rides the bob together */}
      <g className="vh-head">
        <circle cx="40" cy="34" r="17" stroke={stroke} strokeWidth="3.5" />
        {/* brows */}
        <path
          className="vh-brow vh-brow-l"
          d="M30.5 26.5 Q34 24.8 37.2 26.2"
          stroke={stroke}
          strokeWidth="2.2"
          strokeLinecap="round"
        />
        <path
          className="vh-brow vh-brow-r"
          d="M42.8 26.2 Q46 24.8 49.5 26.5"
          stroke={stroke}
          strokeWidth="2.2"
          strokeLinecap="round"
        />
        {/* eyes */}
        <circle className="vh-eye" cx="34" cy="31" r="1.9" fill={stroke} />
        <circle className="vh-eye" cx="46" cy="31" r="1.9" fill={stroke} />
        {/* mouth */}
        {talking ? (
          <ellipse className="vh-jaw" cx="40" cy="40.5" rx="3.4" ry="4.6" fill="#ff90e8" />
        ) : (
          <path d="M36 41 H44" stroke={stroke} strokeWidth="2.6" strokeLinecap="round" />
        )}
        {/* headphones */}
        <path d="M19 30 Q40 6 61 30" stroke="#ff90e8" strokeWidth="3.5" strokeLinecap="round" />
        <rect x="15" y="28" width="8" height="14" rx="3.5" fill="#ff90e8" />
        <rect x="57" y="28" width="8" height="14" rx="3.5" fill="#ff90e8" />
        {/* boom mic */}
        <path d="M19 41 Q12 56 29 54" stroke="#ff90e8" strokeWidth="2.6" strokeLinecap="round" />
        <circle cx="30" cy="54" r="2.6" fill="#ff90e8" />
      </g>
      {/* talking sound arcs */}
      {talking && (
        <g stroke="#ff90e8" strokeWidth="2" strokeLinecap="round">
          <path className="vh-arc" d="M50 36 q3.5 4.5 0 9" opacity="0.9" />
          <path className="vh-arc" d="M55 33 q5.5 7.5 0 15" opacity="0.55" style={{ animationDelay: '0.28s' }} />
        </g>
      )}
    </svg>
  );
}

/* The hero demo — HOLD semantics, one continuous loop: while the streamer talks,
   arriving chat messages queue up silently (held); the moment they stop, TTS
   reads the queue in order. TTS never talks over the streamer. Opens on the
   "held" frame (talking, both messages waiting); reduced motion keeps it still. */
const VH_TICK_MS = 300;
const VH_ARRIVE_AT = [2, 7]; // ticks (while talking) when each chat message lands
const VH_TALK_TICKS = 13; // ~3.9s of the streamer talking
const VH_DONE_TICKS = 6; // ~1.8s beat before the loop restarts
const VH_MESSAGES = [
  { name: 'ggwarrior', color: '#7dd3fc', text: 'that clutch was insane' },
  { name: 'mochi_uwu', color: '#ec4899', text: 'it literally waits for you to stop talking' },
];

function VoiceHoldHeroDemo() {
  // Opens mid-story: streamer talking, both messages already held.
  const [s, setS] = useState({ phase: 'talking', ticks: VH_ARRIVE_AT[1], readMsg: 0, wordIdx: 0 });

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => {
      setS((p) => {
        if (p.phase === 'talking') {
          if (p.ticks + 1 >= VH_TALK_TICKS) return { phase: 'reading', ticks: 0, readMsg: 0, wordIdx: 0 };
          return { ...p, ticks: p.ticks + 1 };
        }
        if (p.phase === 'reading') {
          const words = VH_MESSAGES[p.readMsg].text.split(' ');
          if (p.wordIdx + 1 >= words.length) {
            if (p.readMsg + 1 >= VH_MESSAGES.length) {
              return { phase: 'done', ticks: 0, readMsg: VH_MESSAGES.length, wordIdx: 0 };
            }
            return { ...p, readMsg: p.readMsg + 1, wordIdx: 0 };
          }
          return { ...p, wordIdx: p.wordIdx + 1 };
        }
        if (p.ticks + 1 >= VH_DONE_TICKS) return { phase: 'talking', ticks: 0, readMsg: 0, wordIdx: 0 };
        return { ...p, ticks: p.ticks + 1 };
      });
    }, VH_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const talking = s.phase === 'talking';
  const arrivedCount = talking ? VH_ARRIVE_AT.filter((t) => s.ticks >= t).length : VH_MESSAGES.length;
  const caption = talking
    ? 'You’re talking — TTS holds every message.'
    : s.phase === 'reading'
      ? 'You stop — it reads the queue, in order. Nothing lost.'
      : 'Nothing ever talks over you.';

  return (
    <div className="mx-auto max-w-3xl rounded-2xl border border-gum-pink/30 bg-gum-pink/[0.04] p-2 sm:p-3 shadow-[0_30px_90px_-40px_rgba(255,144,232,0.45)]">
      <div className="rounded-xl border border-gum-gray-800 bg-[#0a0a0a] p-6 md:p-8">
        <div className="flex flex-col sm:flex-row items-center gap-6 md:gap-10">
          {/* the streamer + mic */}
          <div className="flex shrink-0 flex-col items-center gap-2">
            <StreamerFigure talking={talking} />
            <MicBars active={talking} h={18} />
            <span
              className={`text-[10px] font-bold uppercase tracking-wider ${
                talking ? 'text-gum-pink' : 'text-gum-gray-600'
              }`}
            >
              {talking ? 'mic live' : 'mic quiet'}
            </span>
          </div>

          {/* the chat queue — held while talking, read in order after. BOTH
              cards render always — a not-yet-arrived one is `invisible` (space
              reserved) rather than unmounted, so the box never changes height
              and the page never shifts as messages land (owner catch
              2026-08-06); cc-msg is applied only on arrival so the slide-in
              still plays. */}
          <div className="w-full min-w-0 flex-1">
            <div className="flex w-full flex-col justify-center gap-2">
              {VH_MESSAGES.map((m, i) => {
                const arrived = i < arrivedCount;
                const isReading = s.phase === 'reading' && s.readMsg === i;
                const isDone = (s.phase === 'reading' && s.readMsg > i) || s.phase === 'done';
                const words = m.text.split(' ');
                return (
                  <div
                    key={m.name}
                    className={`${arrived ? 'cc-msg' : 'invisible'} rounded-xl border p-3.5 transition-all duration-300 md:p-4 ${
                      isReading
                        ? 'border-gum-pink/40 bg-gum-pink/[0.05]'
                        : isDone
                          ? 'border-gum-gray-800 bg-gum-gray-950'
                          : 'border-gum-gray-800 bg-gum-gray-950 opacity-55'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-bold" style={{ color: m.color }}>
                        {m.name}
                      </span>
                      {isReading ? (
                        <span className="flex items-center gap-1.5 text-gum-pink">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M3 10v4h4l5 5V5L7 10H3z" />
                          </svg>
                          <MicBars active h={11} />
                        </span>
                      ) : isDone ? (
                        <span className="flex items-center gap-1.5 text-gum-gray-500">
                          <Check className="h-3 w-3" />
                          <span className="text-[10px] font-bold uppercase tracking-wider">read</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-gum-gray-400">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                          </svg>
                          <span className="text-[10px] font-bold uppercase tracking-wider">held</span>
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed md:text-base">
                      {isReading ? (
                        words.map((w, wi) => (
                          <span key={wi}>
                            <span
                              className={
                                wi < s.wordIdx
                                  ? 'text-white'
                                  : wi === s.wordIdx
                                    ? 'rounded bg-gum-pink/25 px-1 text-gum-pink'
                                    : 'text-gum-gray-600'
                              }
                            >
                              {w}
                            </span>{' '}
                          </span>
                        ))
                      ) : (
                        <span className={isDone ? 'text-white' : 'text-gum-gray-500'}>{m.text}</span>
                      )}
                    </p>
                  </div>
                );
              })}
            </div>
            <p
              className={`mt-3 h-5 text-sm font-semibold transition-colors ${
                talking ? 'text-gum-pink' : 'text-gum-gray-400'
              }`}
            >
              {caption}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function VoiceHold({ hideHeader = false }) {
  return (
    <section id="voice-hold" className="px-6 py-24 bg-gum-gray-900/40 border-y border-gum-gray-800">
      <div className="max-w-5xl mx-auto">
        {!hideHeader && (
          <div className="text-center mb-12">
            <div className="reveal">
              <span className="inline-block bg-gum-pink/15 text-gum-pink font-bold text-xs px-4 py-1.5 rounded-full uppercase tracking-widest">
                The feature no one else has
              </span>
            </div>
            <h2 className="mt-6 text-5xl md:text-7xl font-black tracking-tight reveal" data-delay="100">
              Voice Hold.
            </h2>
            <p
              className="mt-5 text-base md:text-lg text-gum-gray-400 max-w-xl mx-auto leading-relaxed reveal"
              data-delay="200"
            >
              Every other TTS talks straight over you. Shoosh watches your mic: while you’re talking, chat holds —
              silent, queued — and the moment you stop, TTS reads it back in order. No hotkeys, no mute fumbling — the
              only stream TTS that waits its turn.
            </p>
          </div>
        )}
        <div className="max-w-4xl mx-auto reveal">
          <VoiceHoldHeroDemo />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   DONATIONS — live, integrated, TTS-native
   ──────────────────────────────────────────────────────────────────────── */
// Chip row (ALERT_CHIPS idiom) — the old checkmark list + the tip card's
// fee line folded into one rail of facts; the 3-way display below is the
// section's visual (the "New tip" card mock was cut 2026-08-06, owner:
// redundant + not a real Shoosh surface).
const DONATION_CHIPS = [
  'Stripe Connect — straight to your bank',
  'Just 2.5% + 30¢ flat',
  'Reads itself aloud — no plugins',
  'Pairs with your on-screen alerts',
  'Tip page built into your links hub',
  'Donors can cover fees — keep 100%',
];

/* ── The 3-way tip display (owner spec 2026-08-06): ONE tip — Alice's $10 —
   rendered on every surface it actually lands on, all firing together on a
   shared loop.
   ① MJ's REAL donation alert config reproduced from the local DB (sideRail
     template · slideInRight · 4px white rounded border · green #38de17 fill
     at 0.6 · all three shadows at 0.6 [real _shared.js blur math: card
     0 6px 6px / media 0 4px 5px / text 0 2px 3px] · the wolfclap media,
     mediaLoop off so it plays once and holds its last frame · titleText
     "Thanks for the {amount} tip, {username}!" with {amount} = "$10" per
     alert-payload.js formatDollars) at ~0.7 demo scale.
   ② the Chat Box line — chat-templates/classic.js's TIER-4 LEGENDARY render
     (owner pick 2026-08-06; donations ride the chat pipeline as monetary
     messages, user-instance.js fireDonation): bitsMega entrance, gold/orange
     gradient border + tierShift4 pan, the blurred rainbowGlow halo, impact
     lines, the rainbow $10.00 badge (monetary-format.js), 1.15× text — plus
     the tier's screen shake (globals.css shake-heavy on the mini stage) and
     a particle burst in the canonical T4 palette (lib/tier-colors.js, the
     bits-particle-canvas moment as CSS sparks). Platform mark stays the pink
     Shoosh speech bubble; donor name stays the no-color default gray.
   ③ the Heckle Crowd HERO moment — a tip puts the donor's character center
     stage at ~2× crowd scale (heckle-hero.js): gold ★ DONATION! badge
     (heckle-crowd.js celebrateBadge), the 18-particle confetti burst
     (heckle-identity.js geometry, precomputed at the demo unit), jersey
     nameplate on the back, and the bubble in stripe pink (#ff90e8 =
     PLATFORM_COLOR.stripe — donors carry no chat color).
   Reduced motion: all three resting, no cycle. */
const DONO_SHOW_MS = 5200;
const DONO_GAP_MS = 1300;

// The real CONFETTI geometry (heckle-identity.js) with the --f unit (13px,
// same as the hs-bubble replica) folded in to plain px.
const DONO_F = 13;
const DONO_CONFETTI_COLORS = ['#ff5cc8', '#ffd05c', '#7de3ff', '#ffffff', '#c58bff'];
const DONO_CONFETTI = Array.from({ length: 18 }, (_, i) => {
  const ang = Math.PI * (0.15 + 1.7 * (i / 18));
  const dist = 3.4 + (i % 4) * 1.25;
  return {
    dx: Math.round(Math.cos(ang) * dist * DONO_F),
    dy: Math.round((-Math.abs(Math.sin(ang)) * dist - 0.6) * DONO_F),
    rot: ((i * 137) % 540) - 270,
    color: DONO_CONFETTI_COLORS[i % DONO_CONFETTI_COLORS.length],
  };
});

// The legendary burst — an elliptical ring of sparks off the chat row (wider
// than tall to match the row's shape), colors from the canonical T4 palette.
// Distances must CLEAR the row's own box (~330×90 at the demo size) or the
// burst dies invisibly against the gold fill — hence the wide throw.
const DONO_SPARKS = Array.from({ length: 14 }, (_, i) => {
  const ang = (Math.PI * 2 * i) / 14;
  const dist = 40 + (i % 4) * 22;
  return {
    dx: Math.round(Math.cos(ang) * dist * 1.8),
    dy: Math.round(Math.sin(ang) * dist * 0.8),
    rot: ((i * 137) % 540) - 270,
    color: TIER_COLORS[4][i % TIER_COLORS[4].length],
  };
});

// The Shoosh donation mark — overlay-message.js platformIconPath('stripe'):
// our own pink speech bubble, not a platform logo. (Named DonoMark — ui.js
// already exports an unrelated ShooshMark, the animated wordmark logo.)
function DonoMark({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#ff90e8" aria-hidden="true">
      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
    </svg>
  );
}

function TipAlertStage({ cycle, visible }) {
  const videoRef = useRef(null);

  // Each firing replays the media from the top; mediaLoop is off on the real
  // config, so it plays once and holds its last frame.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !visible) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    video.muted = true;
    video.currentTime = 0;
    video.play().catch(() => {});
  }, [visible, cycle]);

  return (
    <div className="flex h-[250px] items-center justify-center overflow-hidden rounded-xl border border-gum-gray-800 bg-[#0d0d12] px-4">
      {visible && (
        <div key={cycle} className="dono-alert-in">
          <div
            style={{
              width: 'fit-content',
              maxWidth: 240,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              color: 'white',
              fontFamily: 'system-ui, sans-serif',
              background: 'rgba(56, 222, 23, 0.6)',
              border: '4px solid #ffffff',
              borderRadius: 12,
              boxShadow: '0 6px 6px rgba(0, 0, 0, 0.6)',
              padding: '12px 16px',
            }}
          >
            <video
              ref={videoRef}
              src="/landing/tip-wolfclap.mp4"
              poster="/landing/tip-wolfclap-poster.jpg"
              muted
              playsInline
              preload="auto"
              style={{
                maxWidth: 190,
                width: '100%',
                display: 'block',
                borderRadius: 12,
                filter: 'drop-shadow(0 4px 5px rgba(0, 0, 0, 0.6))',
              }}
            />
            <div
              style={{
                fontSize: 17,
                fontWeight: 700,
                lineHeight: 1.25,
                textAlign: 'center',
                textShadow: '0 2px 3px rgba(0, 0, 0, 0.6)',
              }}
            >
              Thanks for the $10 tip, Alice!
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Ambient chat above the tip row — established landing chatters, colored
// names + platform marks like the real classic rows.
const DONO_CONTEXT_ROWS = [
  { name: 'ggwarrior', color: '#7dd3fc', text: 'no way that worked', Icon: TwitchIcon },
  { name: 'mochi_uwu', color: '#ec4899', text: 'someone clip that', Icon: YouTubeIcon },
];

function TipChatStage({ cycle, visible }) {
  // The frame stays still (it's the OBS canvas edge); the SCENE inside shakes —
  // keyed by cycle so the tier's shake-heavy replays with each firing.
  return (
    <div className="h-[250px] overflow-hidden rounded-xl border border-gum-gray-800 bg-[#0d0d12]">
      <div key={cycle} className="dono-shake flex h-full flex-col justify-end px-4 pb-4">
        {DONO_CONTEXT_ROWS.map((r) => (
          <div key={r.name} className="dono-chatrow">
            <span className="dono-picon">
              <r.Icon size={11} />
            </span>
            <span className="dono-name" style={{ color: r.color }}>
              {r.name}
            </span>
            <span className="dono-text">{r.text}</span>
          </div>
        ))}
        {/* fixed slot so the column doesn't jump while the row re-fires */}
        <div style={{ minHeight: 72 }}>
          {visible && (
            <div className="dono-chatrow dono-chatrow--tier4">
              <div className="dono-impact" aria-hidden="true" />
              {DONO_SPARKS.map((p, i) => (
                <span
                  key={i}
                  className="dono-spark"
                  aria-hidden="true"
                  style={{
                    '--cdx': `${p.dx}px`,
                    '--cdy': `${p.dy}px`,
                    '--crot': `${p.rot}deg`,
                    background: p.color,
                  }}
                />
              ))}
              <span className="dono-badge">$10.00</span>
              <span className="dono-picon">
                <DonoMark size={11} />
              </span>
              <span className="dono-name" style={{ color: '#9CA3AF' }}>
                Alice
              </span>
              <span className="dono-text">love the stream — keep it up!</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TipHeckleStage({ cycle, visible }) {
  return (
    <div className="flex h-[250px] items-end justify-center gap-3 overflow-hidden rounded-xl border border-gum-gray-800 bg-[#0d0d12] px-4">
      {/* eslint-disable @next/next/no-img-element -- decorative sprites with varied intrinsic aspects; next/image wants fixed dims */}
      <img
        src="/heckle-cast/penguin.webp"
        alt=""
        style={{ height: 48, width: 'auto', transform: 'translateY(8%)' }}
        draggable={false}
      />
      <div className={`hs-pop relative ${visible ? '' : 'hs-pop-hidden'}`}>
        {/* bottom raised off the class's +10px default so the ★ DONATION!
            badge (floating up from the sprite's head) clears under it */}
        {visible && (
          <div key={`b${cycle}`} className="hs-bubble" style={{ borderColor: '#ff90e8', bottom: 'calc(100% + 34px)' }}>
            <span className="hs-bplat">
              <DonoMark size={13} />
            </span>
            <span className="min-w-0">love the stream — keep it up!</span>
          </div>
        )}
        {visible && (
          <span key={`s${cycle}`} className="dono-celebadge" style={{ color: '#fbbf24' }}>
            ★ DONATION!
          </span>
        )}
        <div key={`f${cycle}`} className="dono-celebpop relative">
          {visible &&
            DONO_CONFETTI.map((p, i) => (
              <span
                key={i}
                className="dono-confetti"
                aria-hidden="true"
                style={{
                  '--cdx': `${p.dx}px`,
                  '--cdy': `${p.dy}px`,
                  '--crot': `${p.rot}deg`,
                  background: p.color,
                }}
              />
            ))}
          <img src="/heckle-cast/luchador.webp" alt="" style={{ height: 104, width: 'auto' }} draggable={false} />
          <span className="dono-jersey">Alice</span>
        </div>
      </div>
      <img
        src="/heckle-cast/raccoon.webp"
        alt=""
        style={{ height: 52, width: 'auto', transform: 'translateY(8%)' }}
        draggable={false}
      />
      {/* eslint-enable @next/next/no-img-element */}
    </div>
  );
}

const DONO_WAYS = [
  {
    title: 'The alert',
    desc: 'Your donation alert, exactly as you built it — your media, chrome, and sound.',
    Stage: TipAlertStage,
  },
  {
    title: 'In Chat Box',
    desc: 'The tip storms into chat with the legendary-tier treatment — gold glow, badge, screen shake.',
    Stage: TipChatStage,
  },
  {
    title: 'In Heckle Crowd',
    desc: 'The donor’s character takes center stage — confetti, nameplate, the works.',
    Stage: TipHeckleStage,
  },
];

function TipTriptych() {
  // One shared firing cycle so all three surfaces show the SAME tip landing
  // at the same moment (that's the story).
  const { cycle, visible } = useShowGapCycle(DONO_SHOW_MS, DONO_GAP_MS);
  return (
    <div className="max-w-6xl mx-auto mt-16">
      <div className="text-center max-w-xl mx-auto mb-10 reveal">
        <h3 className="text-2xl md:text-4xl font-black tracking-tight">
          One tip. <span className="gradient-text">Everywhere at once.</span>
        </h3>
        <p className="mt-3 text-sm md:text-base text-gum-gray-400 leading-relaxed">
          Alice tips $10 — it fires your alert, stands out in chat, and puts her character center stage, all in the same
          moment, all read aloud.
        </p>
      </div>
      <div className="grid md:grid-cols-3 gap-5 stagger">
        {DONO_WAYS.map((w) => (
          <div key={w.title} className="reveal">
            <w.Stage cycle={cycle} visible={visible} />
            <h4 className="mt-4 font-bold text-lg">{w.title}</h4>
            <p className="mt-1.5 text-sm leading-relaxed text-gum-gray-500">{w.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Donations() {
  return (
    <section id="donations" className="px-6 py-24 bg-gum-gray-900/40 border-y border-gum-gray-800">
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto reveal">
          <Eyebrow>Donations · live</Eyebrow>
          <h2 className="mt-5 text-4xl md:text-6xl font-black tracking-tight leading-[1.05]">
            Get paid. <span className="gradient-text">Out loud.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-gum-gray-400 leading-relaxed">
            A real donation rail — not a CashApp link in your bio. Because Shoosh owns the audio, every tip reads itself
            on stream the second it lands. Nobody else does this natively.
          </p>
        </div>
        <div className="mt-8 flex flex-wrap justify-center gap-2 reveal" data-delay="150">
          {DONATION_CHIPS.map((c) => (
            <span
              key={c}
              className="rounded-full border border-gum-gray-700 px-3.5 py-1.5 text-xs font-semibold text-gum-gray-300"
            >
              {c}
            </span>
          ))}
        </div>
        <p className="mt-3 text-center text-[11px] text-gum-gray-600 reveal" data-delay="200">
          powered by Stripe
        </p>
      </div>

      <TipTriptych />
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   TTS ENGINE — modes + per-viewer voices + safe by default
   ──────────────────────────────────────────────────────────────────────── */
const TTS_MODES = [
  {
    title: 'All Messages',
    sub: 'Every message gets read',
    glyph: <PathIcon d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />,
  },
  { title: '@Mention Only', sub: 'Only when they @ you', glyph: <span className="text-2xl font-black">@</span> },
  {
    title: '!tts Command',
    sub: 'Viewers opt in per message',
    glyph: <span className="font-mono text-base font-black">!tts</span>,
  },
  { title: 'Bits & Tips Only', sub: 'Gate TTS behind money', glyph: <PathIcon d="M6 3L1 11l11 10L23 11 18 3H6z" /> },
];

const SAFE_CHIPS = ['Profanity filter', 'Hate speech always blocked', 'URL filter', 'Foreign-script block'];

function PathIcon({ d }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden="true"
    >
      <path d={d} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function TtsEngine() {
  const modesRef = useRef(null);
  useStaggerCycle(modesRef, {
    selector: '.mode-card',
    staggerMs: 150,
    cycleMs: 1250,
    activeClass: 'visible',
    highlightClass: 'tts-highlight',
  });

  return (
    <section id="tts" className="px-6 py-24">
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12 reveal">
          <Eyebrow>TTS engine</Eyebrow>
          <h2 className="mt-5 text-4xl md:text-6xl font-black tracking-tight leading-[1.05]">
            Read every chat. <span className="gradient-text">Your way.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-gum-gray-400 leading-relaxed">
            Pick who gets read and how. Every chatter even gets their own consistent voice, automatically — so your
            regulars sound like themselves, every stream.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto" ref={modesRef}>
          {TTS_MODES.map((m) => (
            <div
              key={m.title}
              className="mode-card rounded-xl border-2 border-gum-gray-700 bg-gum-gray-950 p-6 text-center"
            >
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-gum-pink text-black">
                {m.glyph}
              </div>
              <div className="font-bold text-base mb-1">{m.title}</div>
              <div className="text-sm text-gum-gray-500">{m.sub}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          <div className="rounded-xl border border-gum-gray-800 bg-gum-gray-900/50 p-5 reveal-left">
            <div className="text-sm font-bold mb-1">Per-viewer voices</div>
            <p className="text-xs text-gum-gray-500 mb-3">
              Every chatter gets their own voice — the same one, every stream.
            </p>
            <div className="space-y-2.5">
              {[
                { n: 'mochi_uwu', c: '#ec4899', voice: 'warm' },
                { n: 'ggwarrior', c: '#7dd3fc', voice: 'deep' },
                { n: 'streamsniped', c: '#fbbf24', voice: 'bright' },
              ].map((u, idx) => (
                <div key={u.n} className="flex items-center gap-2.5">
                  <span className="text-sm font-bold w-24 truncate" style={{ color: u.c }}>
                    {u.n}
                  </span>
                  <span className="flex items-end gap-0.5 h-4">
                    {[0, 1, 2, 3, 4, 5].map((b) => (
                      <span
                        key={b}
                        className="w-0.5 rounded-full"
                        style={{
                          height: 16,
                          background: u.c,
                          transformOrigin: 'bottom',
                          animation: `micWave ${0.7 + idx * 0.15}s ease-in-out ${b * 0.08}s infinite`,
                        }}
                      />
                    ))}
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-gum-gray-700 px-2 py-0.5 text-[10px] font-semibold text-gum-gray-400">
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M3 10v4h4l5 5V5L7 10H3z" />
                    </svg>
                    {u.voice} voice
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-gum-gray-800 bg-gum-gray-900/50 p-5 reveal-right">
            <div className="text-sm font-bold mb-3">Safe by default</div>
            <div className="flex flex-wrap gap-2">
              {SAFE_CHIPS.map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-gum-gray-700 px-3 py-1.5 text-xs font-semibold text-gum-gray-300"
                >
                  {c}
                </span>
              ))}
            </div>
            <p className="mt-4 text-xs text-gum-gray-500">
              Plus <span className="font-mono bg-gum-gray-800 px-1.5 py-0.5 rounded text-gum-gray-300">!shh</span> —
              viewers can opt out of any single message.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   HYPE SQUAD — Heckle Crowd + the noise it makes (absorbs the old
   Sounds & Chat FX section; classic scrolling chat gets one aside line).
   ──────────────────────────────────────────────────────────────────────── */
const VE_CHAT_NAMES = [
  { name: 'xQcOW', color: '#ff6b6b' },
  { name: 'pokimane', color: '#ec4899' },
  { name: 'Dunkorslam', color: '#fbbf24' },
  { name: 'ninja', color: '#a855f7' },
  { name: 'shroud', color: '#3b82f6' },
  { name: 'TimTheTatman', color: '#22c55e' },
  { name: 'DrLupo', color: '#f97316' },
  { name: 'chatter_42', color: '#60a5fa' },
  { name: 'ggplayer', color: '#2dd4bf' },
  { name: 'vibe_check', color: '#c084fc' },
];
const VE_CYCLE = [
  { effect: 'big', text: 'LETS GOOOOO' },
  { text: 'absolute unit' },
  { effect: 'small', text: 'quietly absorbing' },
  { sound: 'airhorn', who: 'Dunkorslam' },
  { effect: 'shake', text: 'NO NO NO NO' },
  { text: 'dude.' },
  { effect: 'nod', text: 'big agreed' },
  { text: 'how did you do that' },
  { effect: 'explode', text: 'BOOOOM' },
  { sound: 'bruh', who: 'pokimane' },
  { effect: 'ripple', text: 'stadium wave time', kind: 'letter' },
  { text: 'chat is wilding' },
  { effect: 'bloom', text: 'clutch factor', kind: 'letter' },
  { text: 'OMEGALUL' },
  { effect: 'jitter', text: 'too much caffeine' },
  { sound: 'oof', who: 'chatter_42' },
];
const VE_MAX_VISIBLE = 6;
const VE_STEP_MS = 1100;

function veRandomName() {
  return VE_CHAT_NAMES[Math.floor(Math.random() * VE_CHAT_NAMES.length)];
}
function veBuildMessage(item) {
  const who = item.who ? VE_CHAT_NAMES.find((n) => n.name === item.who) || veRandomName() : veRandomName();
  const el = document.createElement('div');
  const nameSpan = document.createElement('span');
  nameSpan.className = 'name';
  nameSpan.style.color = who.color;
  nameSpan.textContent = who.name;
  const textSpan = document.createElement('span');
  textSpan.className = 'text';
  if (item.sound) {
    el.className = 'mock-msg sound-alert';
    const iconSpan = document.createElement('span');
    iconSpan.className = 'icon';
    iconSpan.textContent = '🔊';
    textSpan.textContent = 'played !' + item.sound;
    el.append(iconSpan, nameSpan, textSpan);
    return el;
  }
  el.className = 'mock-msg';
  if (item.effect) {
    const effectSpan = document.createElement('span');
    effectSpan.className = 'vefx-' + item.effect;
    if (item.kind === 'letter') {
      item.text.split('').forEach((ch, i) => {
        const charSpan = document.createElement('span');
        charSpan.style.setProperty('--char-delay', i * 0.06 + 's');
        charSpan.textContent = ch === ' ' ? '\u00A0' : ch;
        effectSpan.appendChild(charSpan);
      });
    } else {
      effectSpan.textContent = item.text;
    }
    textSpan.appendChild(effectSpan);
  } else {
    textSpan.textContent = item.text;
  }
  el.append(nameSpan, textSpan);
  return el;
}

/* Crowd demo — real cast art from public/heckle-cast. Like the real Heckle
   Crowd: each figure pops up from below the fold at its own moment (randomized
   order across the row, not a wave), faces the camera (front view), turns
   around (back view) to watch the stream — and the chatty ones drop their line
   right as they settle, in a replica of the real overlay bubble (dark pill,
   accent border, platform glyph, no name row — heckle-bubble.js is the
   reference). Once everyone is in, the bubble keeps hopping through the lines.
   Triggers on scroll-into-view; reduced motion = assembled crowd, one still
   bubble. */
const HS_CROWD = [
  { sprite: 'duck', h: 62, at: 5800 },
  { sprite: 'gremlin', h: 78, at: 400 },
  { sprite: 'grandma', h: 72, at: 7600 },
  { sprite: 'taco', h: 58, at: 2600 },
  { sprite: 'dumpster-fire', h: 64, at: 9900 },
  { sprite: 'skeleton', h: 80, at: 4700 },
  { sprite: 'wizard', h: 82, at: 12800 },
  { sprite: 'capybara', h: 56, at: 11300 },
  { sprite: 'pigeon', h: 48, at: 14600 },
];
const HS_LINES = [
  { at: 1, platform: 'twitch', color: '#22c55e', text: 'GET HIM CHAT', fx: 'explode' },
  { at: 4, platform: 'twitch', color: '#fbbf24', text: 'this is fine 🔥' },
  { at: 2, platform: 'tiktok', color: '#ec4899', text: 'get wrecked' },
  { at: 6, platform: 'youtube', color: '#c084fc', text: 'I foresaw that whiff' },
  { at: 5, platform: 'twitch', color: '#7dd3fc', text: 'been in this chat 1000 years' },
  { at: 3, platform: 'youtube', color: '#f97316', text: 'they about a choke!' },
];
const HS_STEP_MS = 2600; // post-assembly: the bubble hops on this cadence
const HS_FLIP_MS = 850; // how long a figure faces the camera before turning
const HS_CHAT_LAG_MS = 200; // the line lands just after the turn completes
const HS_TICK_MS = 150;
// Chat moments ride each speaking figure's arrival, in arrival order.
const HS_MOMENTS = HS_LINES.map((line, li) => ({
  li,
  showFrom: HS_CROWD[line.at].at + HS_FLIP_MS + HS_CHAT_LAG_MS,
})).sort((a, b) => a.showFrom - b.showFrom);
const HS_CYCLE_START = HS_MOMENTS[HS_MOMENTS.length - 1].showFrom + HS_STEP_MS;

const HS_GLYPHS = {
  twitch: <TwitchIcon size={13} />,
  youtube: <YouTubeIcon size={13} />,
  tiktok: <TikTokIcon size={13} />,
};

function HeckleCrowdDemo() {
  const [elapsed, setElapsed] = useState(-1); // ms since the stage scrolled into view; -1 = not yet
  const videoRef = useRef(null);
  const stageRef = useRef(null);

  useEffect(() => {
    // React doesn't reliably emit the muted attribute into SSR'd HTML — set it
    // imperatively so the muted-autoplay policy is satisfied before play().
    // The clip does NOT autoplay on page load: it starts from 0 when the stage
    // first scrolls into view (below), so the viewer sees it from the top.
    const video = videoRef.current;
    if (video) video.muted = true;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const stage = stageRef.current;
    if (!stage) return;
    let timer = null;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          obs.unobserve(e.target);
          if (reduced) {
            setElapsed(HS_CYCLE_START); // assembled, first line up, nothing moving
            return;
          }
          if (video) {
            video.currentTime = 0;
            video.play().catch(() => {});
          }
          const t0 = performance.now();
          timer = setInterval(() => setElapsed(performance.now() - t0), HS_TICK_MS);
        });
      },
      { threshold: 0.35 },
    );
    obs.observe(stage);
    return () => {
      obs.disconnect();
      if (timer) clearInterval(timer);
    };
  }, []);

  const phaseFor = (i) => {
    const { at } = HS_CROWD[i];
    if (elapsed < at) return 'hidden';
    if (elapsed < at + HS_FLIP_MS) return 'front';
    return 'back';
  };
  // Which line is up: each arrival's own chat during the drip, then the hop cycle.
  let bubble = null;
  let bubbleKey = null;
  if (elapsed >= HS_CYCLE_START) {
    const n = Math.floor((elapsed - HS_CYCLE_START) / HS_STEP_MS);
    bubble = HS_LINES[n % HS_LINES.length];
    bubbleKey = `c${n}`;
  } else {
    for (let m = HS_MOMENTS.length - 1; m >= 0; m--) {
      if (elapsed >= HS_MOMENTS[m].showFrom) {
        bubble = HS_LINES[HS_MOMENTS[m].li];
        bubbleKey = `a${m}`;
        break;
      }
    }
  }

  return (
    <div className="mx-auto max-w-[680px] rounded-2xl border border-gum-pink/30 bg-gum-pink/[0.04] p-2 sm:p-3 shadow-[0_30px_90px_-40px_rgba(255,144,232,0.45)]">
      <div
        ref={stageRef}
        className="relative overflow-hidden rounded-xl border border-gum-gray-800"
        style={{ height: 320, background: '#101016' }}
      >
        {/* the streamer's game, running behind the crowd */}
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          src="/landing/heckle-stage.mp4"
          poster="/landing/heckle-stage-poster.jpg"
          preload="auto"
          muted
          loop
          playsInline
        />
        {/* grade + bottom vignette so the crowd and bubbles stay legible */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,10,14,0.12) 0%, rgba(10,10,14,0.25) 55%, rgba(10,10,14,0.55) 100%)',
          }}
        />
        <span className="absolute right-3.5 top-3 z-10 flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.15em] text-gum-gray-500">
          <span
            className="h-1.5 w-1.5 rounded-full bg-red-500 shadow-[0_0_6px_#ef4444]"
            style={{ animation: 'recordPulse 1.5s ease-in-out infinite' }}
          />
          Your stream
        </span>
        {/* soft stage glow behind the crowd */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-40"
          style={{
            background: 'radial-gradient(60% 100% at 50% 100%, rgba(255,144,232,0.1) 0%, rgba(255,144,232,0) 70%)',
          }}
        />
        {/* the crowd — pops up facing you, turns around to watch your stream */}
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-center gap-1.5 px-3">
          {HS_CROWD.map((c, i) => {
            const phase = phaseFor(i);
            return (
              <div key={c.sprite} className={`hs-pop relative shrink-0 ${phase === 'hidden' ? 'hs-pop-hidden' : ''}`}>
                {bubble && bubble.at === i && (
                  <div className="hs-bubble" key={bubbleKey} style={{ borderColor: bubble.color }}>
                    <span className="hs-bplat">{HS_GLYPHS[bubble.platform]}</span>
                    <span className="min-w-0">
                      {bubble.fx ? <span className={`vefx-${bubble.fx}`}>{bubble.text}</span> : bubble.text}
                    </span>
                  </div>
                )}
                <div className="hs-fig" style={{ animationDelay: `${(i % 5) * 0.4}s` }}>
                  <div key={phase === 'back' ? 'back' : 'front'} className={phase === 'back' ? 'hs-turn' : ''}>
                    {/* eslint-disable-next-line @next/next/no-img-element -- decorative sprites with varied intrinsic aspects; next/image wants fixed dims */}
                    <img
                      src={`/heckle-cast/${c.sprite}${phase === 'back' ? '' : '-front'}.webp`}
                      alt=""
                      style={{ height: c.h, width: 'auto', transform: 'translateY(12%)' }}
                      draggable={false}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function HypeSquad() {
  const overlayRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const overlay = overlayRef.current;
    const section = sectionRef.current;
    if (!overlay || !section) return;
    let cycleIdx = 0;
    let cycleTimer = null;
    let currentAudio = null;
    let currentPill = null;
    const pending = new Set();
    const setT = (fn, ms) => {
      const id = setTimeout(() => {
        pending.delete(id);
        fn();
      }, ms);
      pending.add(id);
    };
    function pushNext() {
      overlay.appendChild(veBuildMessage(VE_CYCLE[cycleIdx]));
      cycleIdx = (cycleIdx + 1) % VE_CYCLE.length;
      while (overlay.children.length > VE_MAX_VISIBLE) overlay.removeChild(overlay.firstChild);
    }
    function startCycle() {
      if (cycleTimer) return;
      if (overlay.children.length === 0) for (let i = 0; i < 3; i++) pushNext();
      cycleTimer = setInterval(pushNext, VE_STEP_MS);
    }
    function stopCycle() {
      if (cycleTimer) {
        clearInterval(cycleTimer);
        cycleTimer = null;
      }
    }
    // Reduced motion: a static snapshot of the overlay (no cycling) — the
    // sound pills below stay clickable (user-initiated audio is fine).
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let obs = null;
    if (reduced) {
      for (let i = 0; i < VE_MAX_VISIBLE; i++) pushNext();
    } else {
      obs = new IntersectionObserver(
        (entries) => entries.forEach((e) => (e.isIntersecting ? startCycle() : stopCycle())),
        { threshold: 0.2 },
      );
      obs.observe(section);
    }

    function pillClick(e) {
      const pill = e.currentTarget;
      const cmd = pill.dataset.sound;
      if (!cmd) return;
      if (currentAudio) {
        try {
          currentAudio.pause();
        } catch {}
        if (currentPill) currentPill.classList.remove('playing');
      }
      pill.classList.add('playing');
      currentPill = pill;
      try {
        currentAudio = new Audio('/sounds/builtin/' + cmd + '.mp3');
        currentAudio.volume = 0.15;
        currentAudio.addEventListener('ended', () => {
          pill.classList.remove('playing');
          if (currentPill === pill) currentPill = null;
        });
        currentAudio.addEventListener('error', () => setT(() => pill.classList.remove('playing'), 600));
        currentAudio.play().catch(() => setT(() => pill.classList.remove('playing'), 600));
      } catch {
        setT(() => pill.classList.remove('playing'), 600);
      }
    }
    const pills = section.querySelectorAll('.sound-pill[data-sound]');
    pills.forEach((p) => p.addEventListener('click', pillClick));

    return () => {
      stopCycle();
      if (obs) obs.disconnect();
      pills.forEach((p) => p.removeEventListener('click', pillClick));
      if (currentAudio) {
        try {
          currentAudio.pause();
        } catch {}
      }
      pending.forEach((id) => clearTimeout(id));
    };
  }, []);

  return (
    <section ref={sectionRef} id="hype-squad" className="px-6 py-24">
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12 reveal">
          <Eyebrow>Your hype squad</Eyebrow>
          <h2 className="mt-5 text-4xl md:text-6xl font-black tracking-tight leading-[1.05]">
            Chat doesn’t scroll here.
            <br />
            <span className="gradient-text">It shows up.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-gum-gray-400 leading-relaxed">
            Flip on Heckle Crowd and every chatter walks on stage as a character — 54 of them, gremlin to dumpster fire
            — crowding the edge of your stream and heckling you in speech bubbles while TTS gives them voices.
          </p>
        </div>

        <div className="reveal" data-delay="150">
          <HeckleCrowdDemo />
        </div>

        <div className="mt-16 text-center">
          <h3 className="text-3xl md:text-5xl font-black tracking-tight reveal">
            And they came to <span style={{ color: '#ff3e8e' }}>make noise.</span>
          </h3>
          <p className="mt-4 text-base md:text-lg max-w-xl mx-auto text-gum-gray-400 reveal" data-delay="100">
            8 text effects and 80+ sound effects your viewers can fire straight from chat. No bits. No mods. Just vibes.
          </p>
          <div className="mt-8 reveal" data-delay="200">
            <div className="mock-overlay" ref={overlayRef} />
          </div>
          <div className="reveal" data-delay="300">
            <div className="sound-row-label">Or try one yourself</div>
            <div className="sound-row">
              {['airhorn', 'bruh', 'coin', 'oof', 'clap', 'gameover'].map((s) => (
                <button key={s} type="button" className="sound-pill" data-sound={s}>
                  !{s}
                </button>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-12 text-center text-sm text-gum-gray-500 reveal">
          Prefer a classic scrolling chat? Chat Box mode is one toggle away.
        </p>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   CHAT ARCADE — points → collection → co-op siege (the viewer game loop)
   ──────────────────────────────────────────────────────────────────────── */

// Beat ① — the points wallet, balance ticking up as chat lands.
function WalletMini() {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setTick((t) => t + 1), 1600);
    return () => clearInterval(id);
  }, []);

  const inc = [2, 3, 2, 5][tick % 4];
  const balance = 12480 + [2, 3, 2, 5].slice(0, tick % 4).reduce((a, b) => a + b, 0) + Math.floor(tick / 4) * 12;

  return (
    <div className="w-full max-w-[220px] rounded-xl border border-gum-gray-800 bg-[#0d0d12] p-4 text-center">
      <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-gum-gray-600">Shoosh Points</div>
      <div className="relative mt-1.5">
        <div className="font-mono text-3xl font-black text-gum-pink">{balance.toLocaleString('en-US')}</div>
        <span key={tick} className="arc-float absolute -right-1 top-0 text-xs font-black text-emerald-400">
          +{inc}
        </span>
      </div>
      <div className="mt-2 text-[10px] text-gum-gray-600">earned by chatting</div>
      <div className="mt-2.5 flex items-center justify-center gap-2.5">
        <TwitchIcon size={13} />
        <YouTubeIcon size={13} />
        <TikTokIcon size={13} />
      </div>
    </div>
  );
}

// Beat ② — the pull reveal, cycling real front-view characters.
const PULL_POOL = [
  { sprite: 'axolotl', label: 'THE AXOLOTL', who: 'nightowl', color: '#f0abfc' },
  { sprite: 'mothman', label: 'THE MOTHMAN', who: 'pixelpanda', color: '#a970ff' },
  { sprite: 'keyboard-cat', label: 'KEYBOARD CAT', who: 'ggwarrior', color: '#7dd3fc' },
  { sprite: 'crt-robot', label: 'THE CRT ROBOT', who: 'lofi_lurker', color: '#2dd4bf' },
];

function PullMini() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setIdx((p) => (p + 1) % PULL_POOL.length), 3000);
    return () => clearInterval(id);
  }, []);

  const pull = PULL_POOL[idx];

  return (
    <div key={idx} className="alert-pop flex flex-col items-center gap-2">
      <div
        className="flex h-[110px] w-[110px] items-center justify-center rounded-xl border-2 bg-[#0d0d12]"
        style={{ borderColor: pull.color, boxShadow: `0 0 30px -10px ${pull.color}` }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- decorative sprites with varied intrinsic aspects; next/image wants fixed dims */}
        <img
          src={`/heckle-cast/${pull.sprite}-front.webp`}
          alt=""
          style={{ height: 92, width: 'auto' }}
          draggable={false}
        />
      </div>
      <div className="text-[11px] font-black tracking-wide" style={{ color: pull.color }}>
        ★ CAUGHT {pull.label}!
      </div>
      <div className="text-[10px] text-gum-gray-600">pulled by {pull.who}</div>
    </div>
  );
}

// Beat ③ — the crowd slot swapping to the locked character.
function SwapMini() {
  const [locked, setLocked] = useState(true);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setLocked((p) => !p), 2500);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex flex-col items-center gap-2.5">
      <div className="flex items-end justify-center gap-1.5">
        {/* eslint-disable @next/next/no-img-element -- decorative sprites with varied intrinsic aspects; next/image wants fixed dims */}
        <img src="/heckle-cast/snapback.webp" alt="" style={{ height: 52, width: 'auto' }} draggable={false} />
        <div key={locked ? 'locked' : 'random'} className="hs-turn">
          <img
            src={`/heckle-cast/${locked ? 'mothman' : 'blob'}.webp`}
            alt=""
            style={{ height: 64, width: 'auto' }}
            draggable={false}
          />
        </div>
        <img src="/heckle-cast/pickle.webp" alt="" style={{ height: 50, width: 'auto' }} draggable={false} />
        {/* eslint-enable @next/next/no-img-element */}
      </div>
      <span
        className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold ${
          locked ? 'border-gum-pink/50 text-gum-pink' : 'border-gum-gray-700 text-gum-gray-500'
        }`}
      >
        {locked ? '🔒 locked: the Mothman' : 'random character'}
      </span>
    </div>
  );
}

const ARCADE_BEATS = [
  {
    title: 'Chat to earn',
    desc: 'Chatting on Twitch, YouTube, or TikTok earns Shoosh Points automatically. No commands, no setup — hanging out is the game.',
    Demo: WalletMini,
  },
  {
    title: 'Pull to collect',
    desc: 'Points buy character pulls. Every catch struts across your stream — “nightowl caught the Axolotl!” — and joins their collection.',
    Demo: PullMini,
  },
  {
    title: 'Lock to flex',
    desc: 'Viewers lock in a favorite — from then on, that’s who they are in your crowd, every single stream.',
    Demo: SwapMini,
  },
];

// The siege banner's live loop — a REAL siege recorded off /overlay-preview
// (scripted horde + volleys via the same preview rail the dashboard uses;
// recorder: scripts/record-siege-landing.js, derived from probe-zombie-deaths.js).
function SiegeLoop() {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      video.pause();
      return;
    }
    video.play().catch(() => {});
  }, []);

  return (
    <video
      ref={videoRef}
      src="/landing/siege-live.mp4"
      poster="/landing/siege-live-poster.jpg"
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      className="w-full rounded-xl border border-gum-gray-800"
      aria-label="A live zombie siege on a stream overlay — the horde advancing under pink #tags while chat's characters defend the barricade"
    />
  );
}

export function ChatArcade() {
  return (
    <section id="chat-arcade" className="px-6 py-24 bg-gum-gray-900/40 border-y border-gum-gray-800">
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12 reveal">
          <Eyebrow>The chat arcade</Eyebrow>
          <h2 className="mt-5 text-4xl md:text-6xl font-black tracking-tight leading-[1.05]">
            Your chat just became <span className="gradient-text">a game.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-gum-gray-400 leading-relaxed">
            Viewers earn points for hanging out, spend them on characters, and team up when the zombies come. A reason
            to stay in your chat — not just any chat.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 stagger">
          {ARCADE_BEATS.map((b) => (
            <div key={b.title} className="rounded-2xl border border-gum-gray-800 bg-gum-gray-900/50 p-6 reveal">
              <div className="flex h-[190px] items-center justify-center overflow-hidden rounded-xl border border-gum-gray-800 bg-gum-gray-950">
                <b.Demo />
              </div>
              <h3 className="mt-4 font-bold text-lg">{b.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-gum-gray-500">{b.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-2xl border border-gum-gray-800 bg-gum-gray-900/50 p-6 md:p-8 reveal">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-4xl font-black tracking-tight">Then the zombies come.</h3>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-gum-gray-400">
                A co-op siege right on your overlay: the horde marches on your crowd, chat aims by typing zombie tags,
                and the whole room defends the barricade together — with a pot split for the defenders. Zero setup. It
                plays on the overlay you already added.
              </p>
            </div>
            <SiegeLoop />
          </div>
        </div>
      </div>
    </section>
  );
}
